import { WebsiteAnalysisResult } from '../types';
import { WEBSITE_PRESETS } from '../data/websiteScenarios';

const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

export async function analyzeWebsite(rawUrl: string): Promise<WebsiteAnalysisResult> {
  await delay(800); // DNS and SSL inspection simulation

  let url = rawUrl.trim();
  if (!url.startsWith('http://') && !url.startsWith('https://')) {
    url = 'https://' + url;
  }

  let domain = '';
  try {
    const parsed = new URL(url);
    domain = parsed.hostname;
  } catch (e) {
    throw new Error('Please enter a valid website URL or domain name (e.g. example.com.au).');
  }

  const matched = WEBSITE_PRESETS.find(p => p.domain.toLowerCase() === domain.toLowerCase() || domain.toLowerCase().includes(p.domain.toLowerCase()));

  if (matched) {
    return {
      id: 'web-chk-' + Date.now(),
      url,
      domain: matched.domain,
      riskLevel: matched.riskLevel,
      riskScore: matched.riskScore,
      domainAgeDays: matched.domainAgeDays,
      registrar: matched.registrar,
      hasHttps: matched.hasHttps,
      suspiciousRedirects: matched.suspiciousRedirects,
      potentialImpersonation: matched.potentialImpersonation,
      typosquattingDetected: matched.typosquattingDetected,
      reputationIndicators: {
        sslValid: matched.hasHttps,
        dnssec: matched.riskLevel === 'LOW',
        blacklisted: matched.riskLevel === 'HIGH',
        recentRegistration: matched.domainAgeDays < 30,
        tldRisk: matched.domain.endsWith('.xyz') ? 'high' : matched.domain.endsWith('.gov.au') ? 'low' : 'medium'
      },
      warningSigns: matched.warningSigns,
      safeRecommendations: matched.safeRecommendations,
      checkedAt: new Date().toISOString()
    };
  }

  // Dynamic heuristic for any other domain
  const isGovAu = domain.endsWith('.gov.au') || domain.endsWith('.edu.au');
  const isComAu = domain.endsWith('.com.au') || domain.endsWith('.org.au') || domain.endsWith('.net.au');
  const hasSuspiciousTLD = domain.endsWith('.xyz') || domain.endsWith('.top') || domain.endsWith('.club') || domain.endsWith('.cc');
  const hasBrandWords = ['mygov', 'medicare', 'ato', 'linkt', 'commbank', 'auspost'].some(b => domain.includes(b));

  let score = 20;
  const warningSigns: string[] = [];
  const safeRecommendations: string[] = [];

  if (isGovAu) {
    score = 3;
    safeRecommendations.push('Verified Australian Government / Education namespace (.gov.au is strictly restricted to verified Australian agencies).');
  } else {
    if (hasBrandWords && !isComAu) {
      score += 65;
      warningSigns.push('Contains recognizable Australian corporate/government brand terms on an unofficial domain suffix.');
    }
    if (hasSuspiciousTLD) {
      score += 40;
      warningSigns.push('High-risk top-level domain frequently utilized in transient phishing operations.');
    }
    if (domain.includes('-') && (domain.includes('secure') || domain.includes('portal') || domain.includes('login'))) {
      score += 30;
      warningSigns.push('Domain structure exhibits deceptive credential-harvesting naming patterns.');
    }
  }

  score = Math.min(Math.max(score, 5), 95);
  const riskLevel: 'LOW' | 'MEDIUM' | 'HIGH' = score >= 70 ? 'HIGH' : score >= 35 ? 'MEDIUM' : 'LOW';

  if (riskLevel === 'HIGH') {
    safeRecommendations.push('Avoid submitting passwords, Australian ID numbers, or payment card details.');
    safeRecommendations.push('Verify the authentic website address directly using an independent search engine.');
  } else {
    safeRecommendations.push('Automated security scan indicates normal operational status.');
    safeRecommendations.push('Always check the browser padlock and exact spelling before signing in.');
  }

  return {
    id: 'web-chk-' + Date.now(),
    url,
    domain,
    riskLevel,
    riskScore: score,
    domainAgeDays: isGovAu ? 4200 : hasSuspiciousTLD ? 14 : 650,
    registrar: isGovAu ? 'auDA Australian Domain Administration' : 'Cloudflare Inc. / MarkMonitor',
    hasHttps: true,
    suspiciousRedirects: riskLevel === 'HIGH',
    potentialImpersonation: hasBrandWords && !isGovAu ? 'Brand / Institution Impersonation Risk' : undefined,
    typosquattingDetected: hasBrandWords && !isGovAu,
    reputationIndicators: {
      sslValid: true,
      dnssec: isGovAu,
      blacklisted: riskLevel === 'HIGH',
      recentRegistration: hasSuspiciousTLD,
      tldRisk: hasSuspiciousTLD ? 'high' : isGovAu ? 'low' : 'medium'
    },
    warningSigns,
    safeRecommendations,
    checkedAt: new Date().toISOString()
  };
}
